/**
 * CurationBundlePicker — 큐레이션 zip 다중선택 UI (queue #641 · docs/biz-specs/common-showcase.md
 * §UAT-2026-07-07-R641 §R641-5)
 *
 * `GettingStartedPage` 의 ㉰(카탈로그 zip) 경로를 완결하는 신규 섹션(`#gsg-bundle` 앵커).
 * 선택지 원천은 기존 `GET /api/eraf/showcase/modules`(common-showcase `useModuleCatalog` 재사용
 * · TanStack Query) — SUPERSEDED 노드는 제외한다. 전이 의존 프리뷰는 빌드타임 생성물
 * `common-showcase/data/take-this.json`(#639 `deps.codes`) 를 그대로 union — ★신규 그래프
 * 계산 금지(§R641-2, SSOT 이원화 방지). 다운로드는 `POST /api/eraf/catalog/module-source-bundles`
 * body `{ codes: 선택+전이 union }` → blob(#631 exportModuleSource 와 동일 다운로드 패턴).
 *
 * 5상태: default(목록+프리뷰) · loading(다운로드 진행 · 버튼 spinner) · empty(선택 0 → 버튼
 * disabled) · error(카탈로그 로드 실패 또는 429/502/404 다운로드 실패) · dialog(다운로드 전
 * "포함될 N 모듈 확인" 프리뷰 확정 — 전이 자동 포함을 소비자가 인지 후 진행).
 *
 * queue #682 §UAT-2026-07-14-R682 §R682-5⑤': 초기 선택 상태를 담기 카트(`useScaffoldCart`)
 * 로 프리로드한다 — 이 빌더는 zip 반출 경로라 카트를 읽기만 하고 수정하지 않는다(스캐폴드
 * 마법사와 목적 분리 유지).
 */
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FormDialog, EmptyState, ErrorState } from '@ui-kit';
import { useModuleCatalog } from '../../common-showcase/hooks/useModuleCatalog';
import { useModuleSourceBundle } from '../hooks/useModuleSourceBundle';
import { useScaffoldCart } from '../../_shared/hooks/useScaffoldCart';
import takeThisJson from '../../common-showcase/data/take-this.json';
import type { ApiError, ModuleNode, TakeThisData } from '../../common-showcase/types/showcase';

const TAKE_THIS_DATA = takeThisJson as unknown as TakeThisData;

function downloadBlob(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

const groupHeaderStyle: React.CSSProperties = {
  margin: '0 0 var(--spacing-2) 0',
  fontSize: 'var(--font-size-xs)',
  fontWeight: 700,
  color: 'var(--color-text-secondary)',
  fontFamily: 'var(--font-family-mono)',
  textTransform: 'uppercase',
  letterSpacing: '0.05em',
};

const checkboxRowStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 'var(--spacing-2)',
  fontSize: 'var(--font-size-sm)',
  color: 'var(--color-text-primary)',
  padding: 'var(--spacing-1) 0',
  cursor: 'pointer',
};

export function CurationBundlePicker() {
  const { t, i18n } = useTranslation();
  const isEn = (i18n?.language ?? 'ko').toLowerCase().startsWith('en');
  const { data, isLoading, isError, error, refetch } = useModuleCatalog();
  const cart = useScaffoldCart();
  // §R682-5⑤': 카트 프리로드 — 지연 초기화(useState 콜백)로 첫 렌더부터 반영.
  const [selected, setSelected] = useState<Set<string>>(() => new Set(cart.codes));
  const [dialogOpen, setDialogOpen] = useState(false);
  const { mutate, isPending, error: bundleError, isError: isBundleError, reset } = useModuleSourceBundle();

  // §R641-5: SUPERSEDED(`supersededBy != null`) 는 선택지에서 제외(★`!== null` 금지 — #617 가드).
  const activeNodes = useMemo(
    () => (data?.nodes ?? []).filter((n: ModuleNode) => n.supersededBy == null),
    [data]
  );

  const grouped = useMemo(() => {
    const map = new Map<string, ModuleNode[]>();
    for (const n of activeNodes) {
      if (!map.has(n.artifact)) map.set(n.artifact, []);
      map.get(n.artifact)!.push(n);
    }
    return [...map.entries()].sort(([a], [b]) => a.localeCompare(b));
  }, [activeNodes]);

  // §R641-2: 선택 코드별 take-this.json[code].deps.codes(#639 이행 폐쇄) 를 union.
  // 이미 사용자가 직접 선택한 코드는 "전이" 카운트에서 제외(중복 집계 방지).
  const transitiveCodes = useMemo(() => {
    const result = new Set<string>();
    selected.forEach((code) => {
      const entry = TAKE_THIS_DATA[code];
      entry?.deps.codes.forEach((c) => {
        if (!selected.has(c)) result.add(c);
      });
    });
    return result;
  }, [selected]);

  const totalCodes = useMemo(
    () => [...selected, ...transitiveCodes].sort(),
    [selected, transitiveCodes]
  );

  function toggle(code: string) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(code)) next.delete(code);
      else next.add(code);
      return next;
    });
  }

  function handleDownloadClick() {
    if (selected.size === 0) return;
    setDialogOpen(true);
  }

  function handleConfirmDownload() {
    setDialogOpen(false);
    reset();
    mutate(totalCodes, {
      onSuccess: ({ blob, filename }) => downloadBlob(blob, filename),
    });
  }

  const apiError = bundleError as ApiError | null;
  const statusGuidance =
    apiError?.statusCode === 429
      ? t('gettingStartedGuide.bundle.error.429')
      : apiError?.statusCode === 502
      ? t('gettingStartedGuide.bundle.error.502')
      : apiError?.statusCode === 404
      ? t('gettingStartedGuide.bundle.error.404')
      : null;

  return (
    <section id="gsg-bundle" aria-labelledby="gsg-bundle-heading">
      <h2
        id="gsg-bundle-heading"
        style={{ margin: '0 0 var(--spacing-3) 0', fontSize: 'var(--font-size-lg)', color: 'var(--color-text-primary)' }}
      >
        {t('gettingStartedGuide.bundle.heading')}
      </h2>

      {isLoading && (
        <p style={{ color: 'var(--color-text-muted)', fontSize: 'var(--font-size-sm)' }}>
          {t('gettingStartedGuide.bundle.loadingCatalog')}
        </p>
      )}

      {isError && (
        <ErrorState
          title={t('gettingStartedGuide.bundle.catalogErrorTitle')}
          message={error?.message}
          onRetry={() => { void refetch(); }}
        />
      )}

      {!isLoading && !isError && (
        <>
          {activeNodes.length === 0 ? (
            <EmptyState title={t('gettingStartedGuide.bundle.empty')} />
          ) : (
            <div
              role="group"
              aria-label={t('gettingStartedGuide.bundle.groupBy')}
              style={{
                border: '1px solid var(--color-surface-border)',
                borderRadius: 'var(--radius-md)',
                padding: 'var(--spacing-3) var(--spacing-4)',
                maxHeight: 360,
                overflowY: 'auto',
                background: 'var(--color-surface-elevated)',
              }}
            >
              {grouped.map(([artifact, nodes]) => (
                <div key={artifact} style={{ marginBottom: 'var(--spacing-3)' }}>
                  <p style={groupHeaderStyle}>{artifact}</p>
                  {nodes.map((n) => (
                    <label key={n.code} style={checkboxRowStyle}>
                      <input
                        type="checkbox"
                        checked={selected.has(n.code)}
                        onChange={() => toggle(n.code)}
                      />
                      <code style={{ fontFamily: 'var(--font-family-mono)', fontSize: 'var(--font-size-xs)', color: 'var(--color-brand-primary)', minWidth: 40 }}>
                        {n.code}
                      </code>
                      <span>{isEn && n.nameEn?.trim() ? n.nameEn : n.nameKo}</span>
                    </label>
                  ))}
                </div>
              ))}
            </div>
          )}

          {/* 결과 프리뷰 — "선택 N + 전이 M = 총 N+M 모듈 포함" (§R641-5) */}
          <p aria-live="polite" style={{ marginTop: 'var(--spacing-3)', fontSize: 'var(--font-size-sm)', color: 'var(--color-text-secondary)' }}>
            {selected.size === 0
              ? t('gettingStartedGuide.bundle.empty')
              : t('gettingStartedGuide.bundle.preview.count', {
                  selected: selected.size,
                  transitive: transitiveCodes.size,
                  total: totalCodes.length,
                })}
          </p>
          {transitiveCodes.size > 0 && (
            <p style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-muted)', marginTop: 'var(--spacing-1)' }}>
              {t('gettingStartedGuide.bundle.preview.transitiveNote', { codes: [...transitiveCodes].sort().join(', ') })}
            </p>
          )}

          <button
            type="button"
            onClick={handleDownloadClick}
            disabled={selected.size === 0 || isPending}
            aria-busy={isPending}
            style={{
              marginTop: 'var(--spacing-3)',
              display: 'inline-flex',
              alignItems: 'center',
              gap: 'var(--spacing-2)',
              padding: 'var(--spacing-2) var(--spacing-4)',
              background: selected.size === 0 || isPending ? 'var(--color-surface-elevated)' : 'var(--color-brand-primary)',
              color: selected.size === 0 || isPending ? 'var(--color-text-secondary)' : 'var(--color-text-inverse)',
              border: selected.size === 0 || isPending ? '1px solid var(--color-surface-border)' : 'none',
              borderRadius: 'var(--radius-md)',
              fontSize: 'var(--font-size-sm)',
              fontWeight: 600,
              cursor: selected.size === 0 || isPending ? 'not-allowed' : 'pointer',
              fontFamily: 'var(--font-family-sans)',
            }}
          >
            {isPending ? t('gettingStartedGuide.bundle.downloading') : t('gettingStartedGuide.bundle.download')}
          </button>

          {isBundleError && apiError && (
            <div style={{ marginTop: 'var(--spacing-3)' }}>
              <ErrorState
                title={apiError.code || t('gettingStartedGuide.bundle.errorTitle')}
                message={statusGuidance ? `${apiError.message} ${statusGuidance}` : apiError.message}
                onRetry={handleDownloadClick}
              />
            </div>
          )}
        </>
      )}

      <FormDialog
        open={dialogOpen}
        title={t('gettingStartedGuide.bundle.confirm.title', { count: totalCodes.length })}
        onClose={() => setDialogOpen(false)}
        onSubmit={handleConfirmDownload}
        submitLabel={t('gettingStartedGuide.bundle.download')}
        cancelLabel={t('gettingStartedGuide.bundle.cancel')}
        submitTestId="gsg-bundle-confirm-download"
      >
        <ul style={{ margin: 0, paddingLeft: 'var(--spacing-4)' }}>
          {totalCodes.map((code) => (
            <li key={code} style={{ fontSize: 'var(--font-size-sm)', color: 'var(--color-text-primary)' }}>
              <code style={{ fontFamily: 'var(--font-family-mono)', color: 'var(--color-brand-primary)' }}>{code}</code>
              {!selected.has(code) && (
                <span style={{ marginLeft: 'var(--spacing-2)', fontSize: 'var(--font-size-xs)', color: 'var(--color-text-muted)' }}>
                  ({t('gettingStartedGuide.bundle.preview.transitiveBadge')})
                </span>
              )}
            </li>
          ))}
        </ul>
      </FormDialog>
    </section>
  );
}
